import React, { useState, useContext, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Checkbox,
  Button,
  FormControlLabel,
  CircularProgress,
  Card,
  CardContent,
  Icon,
  Box,
} from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import { useQuery, useMutation } from 'react-apollo';
import { GlobalContext } from '../../global-context';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(12),
    backgroundColor: '#f8f9fa',
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    position: 'relative',
    display: 'inline-block',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: -8,
      left: 0,
      width: 40,
      height: 3,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
    },
  },
  checklistItem: {
    margin: theme.spacing(1, 0),
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  checkboxRoot: {
    marginRight: theme.spacing(1),
    marginTop: '-2px',
  },
  checkboxLabel: {
    marginLeft: 0,
    width: '100%',
  },
  infoCard: {
    height: '100%',
    marginBottom: theme.spacing(2),
  },
  infoValue: {
    fontWeight: 500,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
    position: 'fixed',
    bottom: theme.spacing(3),
    left: 0,
    right: 0,
    padding: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 1000,
    boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
  },
  cancelButton: {
    padding: theme.spacing(2, 6),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '200px',
    height: '64px',
  },
  finishButton: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    padding: theme.spacing(2, 6),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '200px',
    height: '64px',
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  startButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(2, 6),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '200px',
    height: '64px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  centeredContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  statusIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
  },
  messageText: {
    marginBottom: theme.spacing(3),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const GET_BOOTH_AND_ORDER_QUERY = gql`
  query getBoothAndOrderForInbound($boothId: bigint!) {
    booths_by_pk(id: $boothId) {
      id
      sequence
      key_number
      ipad_udid
      active
      battery_level
      build_date
      orders(where: { active: { _eq: true } }, limit: 1, order_by: { event_date: desc }) {
        id
        status
        event_name
        event_date
        return_date
        print_count
        inbound_tracking
        inbound_shipment_activity {
          delivered
          latest_status_description
          updatedat
        }
      }
    }
  }
`;

const START_INBOUND_MUTATION = gql`
  mutation startInbound($orderId: bigint!) {
    update_orders_by_pk(pk_columns: { id: $orderId }, _set: { status: "Checkin" }) {
      id
      status
    }
  }
`;

const COMPLETE_INBOUND_MUTATION = gql`
  mutation completeInbound($orderId: bigint!) {
    update_orders_by_pk(pk_columns: { id: $orderId }, _set: { status: "Done" }) {
      id
      status
    }
  }
`;

function BoothInbound(props) {
  const classes = useStyles();
  const ctx = useContext(GlobalContext);
  const history = useHistory();
  const boothId = parseInt(props.match.params.id);

  const { loading, error, data, refetch } = useQuery(GET_BOOTH_AND_ORDER_QUERY, {
    variables: { boothId },
    fetchPolicy: 'network-only',
    pollInterval: 5000, // Poll every 5 seconds for updates
  });

  // Initialize checklist with default values
  const [checklist, setChecklist] = useState({
    verifyBooth: false,
    scrapeStickers: false,
    verifyCaseContents: false,
    cleanEquipment: false,
    checkTripods: false,
    checkPrinter: false,
    ipadAdminMode: false,
    verifyPhotos: false,
    pullBoothConfig: false,
    packCaseContents: false,
    readyForNextEvent: false,
  });

  // Get booth and order data when available
  const booth = data?.booths_by_pk;
  const order = booth?.orders && booth.orders.length > 0 ? booth.orders[0] : null;
  const orderId = order?.id || null;

  // Generate a unique storage key for this booth/order combination
  const storageKey = orderId ? `inbound_checklist_booth_${boothId}_order_${orderId}` : null;

  // Load saved checklist from localStorage when component mounts or order data becomes available
  useEffect(() => {
    if (storageKey && order && order.status === 'Checkin') {
      try {
        const savedChecklist = localStorage.getItem(storageKey);
        if (savedChecklist) {
          const parsedChecklist = JSON.parse(savedChecklist);
          // Ensure all expected keys exist in the loaded checklist
          const mergedChecklist = { ...checklist };
          let isValid = true;

          // Validate the structure of the saved checklist
          Object.keys(checklist).forEach(key => {
            if (typeof parsedChecklist[key] === 'boolean') {
              mergedChecklist[key] = parsedChecklist[key];
            } else {
              isValid = false;
            }
          });

          if (isValid) {
            setChecklist(mergedChecklist);
          } else {
            // If invalid structure, clear the stored data
            localStorage.removeItem(storageKey);
          }
        }
      } catch (e) {
        console.error('Error parsing saved checklist:', e);
        localStorage.removeItem(storageKey);
      }
    }
  }, [storageKey, order]);

  // Update checkPrinter state based on order data
  useEffect(() => {
    if (order && order.print_count === 0) {
      setChecklist(prevState => {
        const newState = {
          ...prevState,
          checkPrinter: true,
        };

        // Update localStorage with the new state if we're in Checkin status
        if (storageKey && order.status === 'Checkin') {
          try {
            localStorage.setItem(storageKey, JSON.stringify(newState));
          } catch (e) {
            console.error('Error saving checklist to localStorage:', e);
          }
        }

        return newState;
      });
    }
  }, [order, storageKey]);

  const [startInbound, { loading: startLoading }] = useMutation(START_INBOUND_MUTATION, {
    onCompleted: () => {
      ctx.handleNotifications(true, 'success', `Inbound process started for Booth #${boothId}`);
      refetch();
    },
    onError: err => {
      ctx.handleNotifications(true, 'error', `Failed to start inbound process: ${err.message}`);
    },
  });

  const [completeInbound, { loading: completeLoading }] = useMutation(COMPLETE_INBOUND_MUTATION, {
    onCompleted: () => {
      // Clear the saved checklist when inbound is complete
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
      ctx.handleNotifications(true, 'success', `Booth #${boothId} has been successfully inbounded.`);
      history.push(`/booths/${boothId}`);
    },
    onError: err => {
      ctx.handleNotifications(true, 'error', `Failed to complete inbound process: ${err.message}`);
    },
  });

  const handleChecklistChange = event => {
    const newChecklist = {
      ...checklist,
      [event.target.name]: event.target.checked,
    };

    setChecklist(newChecklist);

    // Save checklist to localStorage whenever it changes
    if (storageKey && order?.status === 'Checkin') {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newChecklist));
      } catch (e) {
        console.error('Error saving checklist to localStorage:', e);
      }
    }
  };

  const allTasksCompleted = Object.values(checklist).every(item => item === true);

  if (loading) {
    return (
      <CircularProgress
        style={{
          display: 'block',
          margin: 'auto',
        }}
      />
    );
  }

  if (error) {
    return (
      <Paper className={classes.paper}>
        <Typography color='error'>Error loading booth details: {error.message}</Typography>
        <Button variant='contained' color='primary' component={Link} to={`/booths/${boothId}`}>
          Back to Booth
        </Button>
      </Paper>
    );
  }

  if (!booth) {
    return (
      <Paper className={classes.paper}>
        <Typography variant='h5'>Booth not found</Typography>
        <Button variant='contained' color='primary' component={Link} to='/booths' className={classes.buttonContainer}>
          Back to Booths
        </Button>
      </Paper>
    );
  }

  if (!order) {
    return (
      <div className={classes.root}>
        <Typography variant='h4' className={classes.title}>
          <Icon style={{ marginRight: '8px' }}>inventory_2</Icon>
          Booth #{booth.id} Inbound
        </Typography>

        <Paper className={classes.paper}>
          <div className={classes.centeredContent}>
            <Icon className={classes.statusIcon} color='action'>
              info
            </Icon>
            <Typography variant='h5' gutterBottom>
              This booth is not available for inbounding
            </Typography>
            <Typography variant='body1' className={classes.messageText}>
              This booth is not associated with any active orders.
            </Typography>
            <div className={classes.actionButtons}>
              <Button variant='outlined' color='primary' component={Link} to={`/booths/${boothId}`}>
                Back to Booth
              </Button>
            </div>
          </div>
        </Paper>
      </div>
    );
  }

  // Render booth info card
  const renderBoothInfoCard = () => (
    <Paper className={classes.paper} style={{ marginBottom: '16px' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Order ID:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>#{order.id}</span>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Booth ID:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
              #{booth.id} (Seq: {booth.sequence})
            </span>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Event Name:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>{order.event_name}</span>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Type:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
              {order.print_count > 0 ? `Print (${order.print_count} prints)` : 'Digital'}
            </span>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Return Date:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
              {order.return_date ? moment(order.return_date).format('MM/DD/YYYY') : 'N/A'}
            </span>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Tracking:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>{order.inbound_tracking || 'N/A'}</span>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant='body2' color='textSecondary' style={{ fontWeight: 'bold' }}>
            Delivered:
            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
              {order.inbound_shipment_activity && order.inbound_shipment_activity.delivered ? 'Yes' : 'No'}
            </span>
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  // Render by order status
  if (order.status === 'Returned') {
    return (
      <div className={classes.root}>
        <Typography variant='h4' className={classes.title}>
          <Icon style={{ marginRight: '8px' }}>inventory_2</Icon>
          Start Inbound Process for Booth #{booth.id}
        </Typography>

        {renderBoothInfoCard()}

        <Paper className={classes.paper}>
          <div className={classes.centeredContent}>
            <Icon className={classes.statusIcon} color='primary'>
              assignment_return
            </Icon>
            <Typography variant='h5' gutterBottom>
              Booth #{booth.id} is ready for inbound processing
            </Typography>
            <Typography variant='body1' className={classes.messageText}>
              The booth has been returned and is ready to be checked in for the inbound process. Click the button below
              to start inbounding this booth.
            </Typography>

            <Button
              variant='contained'
              className={classes.startButton}
              disabled={startLoading}
              onClick={() => {
                startInbound({
                  variables: {
                    orderId: order.id,
                  },
                });
              }}
            >
              {startLoading ? (
                <>
                  <CircularProgress size={24} style={{ marginRight: '8px', color: 'white' }} />
                  Processing...
                </>
              ) : (
                <>
                  <Icon style={{ marginRight: '8px' }}>play_arrow</Icon>
                  Start Inbound
                </>
              )}
            </Button>
          </div>
        </Paper>

        <div className={classes.buttonContainer}>
          <Button
            variant='outlined'
            color='default'
            component={Link}
            to={`/booths/${boothId}`}
            className={classes.cancelButton}
          >
            Back to Booth
          </Button>
        </div>
      </div>
    );
  } else if (order.status === 'Checkin') {
    return (
      <div className={classes.root}>
        <Typography variant='h4' className={classes.title}>
          <Icon style={{ marginRight: '8px' }}>inventory_2</Icon>
          Inbound Process for Booth #{booth.id}
        </Typography>

        {renderBoothInfoCard()}

        <Typography variant='h5' className={classes.sectionTitle}>
          Inbound Checklist
        </Typography>
        <Paper className={classes.paper}>
          <Typography variant='body2' color='textSecondary' paragraph>
            Please check all items in the booth and complete each step below before finishing the inbound process.
          </Typography>

          {/* 1. Verify Booth ID */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.verifyBooth}
              onChange={handleChecklistChange}
              name='verifyBooth'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Verify Booth ID</strong> - Confirm this is the correct booth by checking the ID and sequence
              number match the information above.
            </Typography>
          </div>

          {/* 2. Scrape Shipping Stickers */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.scrapeStickers}
              onChange={handleChecklistChange}
              name='scrapeStickers'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Scrape Shipping Stickers</strong> - Remove all old shipping labels and stickers from the exterior
              of the booth case.
            </Typography>
          </div>

          {/* 3. Verify Case Contents */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.verifyCaseContents}
              onChange={handleChecklistChange}
              name='verifyCaseContents'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Verify Case Contents</strong> - Check that all standard components are present in the case: 2
              tripods, 1 backdrop crossbar, iPad assembly, main power cord, iPad charger, and backdrop. If it's a print
              booth, also verify the printer, printer power cord, and a roll of paper.
            </Typography>
          </div>

          {/* 4. Clean Equipment */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.cleanEquipment}
              onChange={handleChecklistChange}
              name='cleanEquipment'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Clean All Components</strong> - Pull out all contents from the case. Wipe down each component
              thoroughly with cleaner, including the iPad, stands, printer (if applicable), and booth enclosure.
            </Typography>
          </div>

          {/* 5. Check Tripods */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.checkTripods}
              onChange={handleChecklistChange}
              name='checkTripods'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Check Tripods</strong> - Ensure there are no broken hand-tightening screws and that all
              telescoping parts work correctly. Test stability of each tripod.
            </Typography>
          </div>

          {/* 6. Verify Printer (Conditional) */}
          {order.print_count > 0 && (
            <div className={classes.checklistItem}>
              <Checkbox
                checked={checklist.checkPrinter}
                onChange={handleChecklistChange}
                name='checkPrinter'
                color='primary'
                className={classes.checkboxRoot}
              />
              <Typography>
                <strong>Verify Printer</strong> - Confirm that the printer and power cord were returned. Test the
                printer connection and ensure it has sufficient ink for the next event.
              </Typography>
            </div>
          )}

          {/* 7. iPad Admin Mode */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.ipadAdminMode}
              onChange={handleChecklistChange}
              name='ipadAdminMode'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>iPad Admin Mode & Photo Upload Prep</strong> - Turn on the iPad, enter admin mode in the
              photobooth app, and navigate to the 'INBOUND' or 'Photo Upload' section.
            </Typography>
          </div>

          {/* 8. Verify Photo Upload */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.verifyPhotos}
              onChange={handleChecklistChange}
              name='verifyPhotos'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Verify All Photos Uploaded</strong> - Ensure all photos have been uploaded from the booth
              software. Confirm there are no photos left on the device that aren't visible in the order screen. Ensure
              all pictures are uploaded.
              {order.sessions && (
                <span style={{ display: 'block', marginTop: '4px', color: '#1976d2' }}>
                  Database shows {order.sessions.length} photo sessions for this order.
                </span>
              )}
            </Typography>
          </div>

          {/* 9. Reset Booth Configuration */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.pullBoothConfig}
              onChange={handleChecklistChange}
              name='pullBoothConfig'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Reset Booth Configuration (Pull Booth Config)</strong> - Perform a "pull booth config" in the app
              to clear any event settings and photos on the photobooth hardware. This prepares the booth for its next
              assignment.
            </Typography>
          </div>

          {/* 10. Pack Case Contents */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.packCaseContents}
              onChange={handleChecklistChange}
              name='packCaseContents'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Refold Backdrop & Pack Case</strong> - Neatly refold the backdrop and place it back into the case.
              Securely pack all other cleaned components (tripods, iPad assembly, printer, chargers, crossbar) into
              their designated spots in the case. Return all other components to case.
            </Typography>
          </div>

          {/* 11. Ready for Next Event */}
          <div className={classes.checklistItem}>
            <Checkbox
              checked={checklist.readyForNextEvent}
              onChange={handleChecklistChange}
              name='readyForNextEvent'
              color='primary'
              className={classes.checkboxRoot}
            />
            <Typography>
              <strong>Ready for Next Event</strong> - Verify all steps above are complete and the booth is fully
              prepared for the next customer. Battery is charged and all components are working properly.
            </Typography>
          </div>
        </Paper>

        <div className={classes.buttonContainer}>
          <Button
            variant='outlined'
            color='default'
            component={Link}
            to={`/booths/${boothId}`}
            className={classes.cancelButton}
          >
            Pause Inbound
          </Button>

          <Button
            variant='contained'
            className={classes.finishButton}
            disabled={!allTasksCompleted || completeLoading}
            onClick={() => {
              completeInbound({
                variables: {
                  orderId: order.id,
                },
              });
            }}
          >
            {completeLoading ? (
              <>
                <CircularProgress size={24} style={{ marginRight: '8px', color: 'white' }} />
                Processing...
              </>
            ) : (
              'Finish Inbound'
            )}
          </Button>
        </div>
      </div>
    );
  } else {
    // Any other status
    let statusMessage = '';
    if (order.status === 'Done') {
      statusMessage = 'This booth has already been inbounded and is ready for its next assignment.';
    } else {
      statusMessage = `This booth is currently in '${order.status}' status and is not ready for inbounding.`;
    }

    return (
      <div className={classes.root}>
        <Typography variant='h4' className={classes.title}>
          <Icon style={{ marginRight: '8px' }}>inventory_2</Icon>
          Booth #{booth.id} Inbound
        </Typography>

        <Paper className={classes.paper}>
          <div className={classes.centeredContent}>
            <Icon className={classes.statusIcon} color='action'>
              info
            </Icon>
            <Typography variant='h5' gutterBottom>
              This booth is not available for inbounding
            </Typography>
            <Typography variant='body1' className={classes.messageText}>
              {statusMessage}
            </Typography>
            <div className={classes.actionButtons}>
              <Button variant='outlined' color='primary' component={Link} to={`/booths/${boothId}`}>
                Back to Booth
              </Button>
              <Button variant='contained' color='primary' component={Link} to={`/orders/${order.id}`}>
                View Order
              </Button>
            </div>
          </div>
        </Paper>
      </div>
    );
  }
}

export default BoothInbound;
